import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { downloadBlobUrlFileToBrowser } from '@highlander/common/helpers/file.helpers';
import { CreateFileRequest, FileResponse } from '@models/index';
import { Product, ProductsListItem, ProductUpdateRequest } from '@models/products-and-orders/products';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { BlobStorageService } from 'cui-components';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { FilesService } from '../../file-management/files/files.service';
import {
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  getAll,
  predicateForSorting,
  sortAlphabetically,
  StateDictionary,
  updateEntity,
} from '../../helpers';
import { ProductsService } from './products.service';

type ProductsState = {
  products: ProductsListItem[];
  isLoading: boolean;
  selectedProduct: Product | null;
  selectedProductLoading: boolean;
  genericFiles: FileResponse[];
  genericFilesLoading: boolean;
  selectedGenericFile: FileResponse | null;
  selectedGenericFileLoading: boolean;
  genericFileCreateLoading: boolean;
};

const initialState: ProductsState = {
  products: [],
  isLoading: false,
  selectedProduct: null,
  selectedProductLoading: false,
  genericFiles: [],
  genericFilesLoading: false,
  selectedGenericFile: null,
  selectedGenericFileLoading: false,
  genericFileCreateLoading: false,
};

const productsDictionary: StateDictionary<ProductsListItem> = {
  entityList: 'products',
  listLoading: 'isLoading',
  selectedEntity: 'selectedProduct',
  selectedEntityLoading: 'selectedProductLoading',
  entityId: 'id',
  sortFn: (a, b) => a.name.localeCompare(b.name),
  // unused
  createLoading: '',
};

export const ProductsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('products-store'),

  withComputed(store => ({
    selectedProductId: computed(() => store.selectedProduct()?.id || null),
    selectedGenericFileId: computed(() => store.selectedGenericFile()?.id || null),
  })),

  withMethods(
    (
      store,
      productsService = inject(ProductsService),
      router = inject(Router),
      filesService = inject(FilesService),
      blobStorageService = inject(BlobStorageService),
    ) => {
      createStateResetHandler<ProductsState>('/products')(store, initialState);
      createCustomStateSetOnUrlHandler<ProductsState>('/products', { selectedProduct: null })(store);

      const repositoryMethods = {
        getAllProducts: getAll<ProductsListItem, boolean | undefined>({
          store,
          service: productsService,
          dictionary: productsDictionary,
        }),
        getOneProduct: rxMethod<{ id: string; includeMarkedForDeletion?: boolean }>(
          pipe(
            tap(() => patchState(store, { selectedProductLoading: true })),
            switchMap(({ id, includeMarkedForDeletion }) =>
              productsService.getOne(id, includeMarkedForDeletion ?? false).pipe(
                tap(product => {
                  patchState(store, {
                    selectedProduct: product,
                    selectedProductLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, { selectedProductLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        updateProduct: updateEntity<Product, ProductUpdateRequest>({
          store,
          service: productsService,
          dictionary: productsDictionary,
          onSuccessCallback: (product: Product) => {
            patchState(store, { selectedProduct: product });
          },
        }),

        activateProduct: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { selectedProductLoading: true })),
            switchMap(id =>
              productsService.activate(id).pipe(
                tap(product => {
                  patchState(store, {
                    selectedProduct: product,
                    selectedProductLoading: false,
                    products: store.products().map(p => (p.id === product.id ? product : p)),
                  });
                  router.navigate(['products', product.id, 'details']);
                }),
                catchError(() => {
                  patchState(store, { selectedProductLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        deactivateProduct: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { selectedProductLoading: true })),
            switchMap(id =>
              productsService.deactivate(id).pipe(
                tap(product => {
                  patchState(store, {
                    selectedProduct: product,
                    selectedProductLoading: false,
                    products: store.products().map(p => (p.id === product.id ? product : p)),
                  });
                  router.navigate(['products', product.id, 'details']);
                }),
                catchError(() => {
                  patchState(store, { selectedProductLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        getAllGenericFiles: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { genericFilesLoading: true })),
            switchMap(id =>
              productsService.getAllGenericFiles(id).pipe(
                tap(files =>
                  patchState(store, {
                    genericFiles: sortAlphabetically(files, predicateForSorting('fileName')),
                    genericFilesLoading: false,
                  }),
                ),
                catchError(() => {
                  patchState(store, { genericFilesLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        getOneGenericFile: rxMethod<{ productId: string; fileId: string }>(
          pipe(
            tap(() => patchState(store, { selectedGenericFileLoading: true })),
            switchMap(({ productId, fileId }) =>
              productsService.getOneGenericFile(productId, fileId).pipe(
                tap(file => patchState(store, { selectedGenericFile: file, selectedGenericFileLoading: false })),
                catchError(() => {
                  patchState(store, { selectedGenericFileLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        addGenericFile: rxMethod<{ fileReq: CreateFileRequest; file: File }>(
          pipe(
            tap(() => patchState(store, { genericFileCreateLoading: true, isLoading: true })),
            switchMap(({ fileReq, file }) =>
              filesService.add(fileReq).pipe(
                switchMap(res =>
                  blobStorageService.uploadFile(res.uploadURL, file).pipe(
                    switchMap(() =>
                      filesService.activate(res.id).pipe(
                        tap(fileRes => {
                          patchState(store, {
                            genericFileCreateLoading: false,
                            isLoading: false,
                            genericFiles: sortAlphabetically([...store.genericFiles(), fileRes], predicateForSorting('fileName')),
                            selectedGenericFile: fileRes,
                          });
                          router.navigate(['products', store.selectedProductId(), 'generic-files', fileRes.id, 'details']);
                        }),
                      ),
                    ),
                  ),
                ),
                catchError(() => {
                  patchState(store, { genericFileCreateLoading: false, isLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        downloadGenericFile: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { selectedGenericFileLoading: true })),
            switchMap(fileId =>
              filesService.getDownloadUrl(fileId).pipe(
                tap(file => {
                  patchState(store, { selectedGenericFileLoading: false });
                  downloadBlobUrlFileToBrowser(file.URL);
                }),
                catchError(() => {
                  patchState(store, { selectedGenericFileLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        deleteGenericFile: rxMethod<{ productId: string; fileId: string }>(
          pipe(
            tap(() => patchState(store, { genericFilesLoading: true })),
            switchMap(({ productId, fileId }) =>
              filesService.delete(fileId).pipe(
                tap(() => {
                  patchState(store, { genericFilesLoading: false });
                  router.navigate(['products', productId, 'generic-files']);
                  repositoryMethods.getAllGenericFiles(productId);
                }),
                catchError(() => {
                  patchState(store, { genericFilesLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        resetState: (state: Partial<ProductsState>) => patchState(store, state),
      };

      const navigationActions = {
        goToProductActions: async (id: string): Promise<boolean> => {
          const result = await router.navigate(['products', id]);

          if (!result) return false;

          if (!store.selectedProductLoading()) repositoryMethods.getOneProduct({ id });

          return true;
        },

        goToListPage: (): void => {
          patchState(store, { selectedProduct: null });
          router.navigate(['products']);
        },

        goToGenericFileActions: (fileId: string): void => {
          repositoryMethods.getOneGenericFile({ productId: store.selectedProductId()!, fileId });
          router.navigate(['products', store.selectedProductId(), 'generic-files', fileId]);
        },

        goToGenericFileListPage: (): void => {
          patchState(store, { selectedGenericFile: null });
          router.navigate(['products', store.selectedProductId(), 'generic-files']);
        },
      };

      return {
        ...repositoryMethods,
        ...navigationActions,
      };
    },
  ),
);

export interface ProductsStoreType {
  readonly products: Signal<ProductsListItem[]>;
  readonly isLoading: Signal<boolean>;
  readonly selectedProduct: Signal<Product | null>;
  readonly selectedProductLoading: Signal<boolean>;
  readonly genericFiles: Signal<FileResponse[]>;
  readonly genericFilesLoading: Signal<boolean>;
  readonly selectedGenericFile: Signal<FileResponse | null>;
  readonly selectedGenericFileLoading: Signal<boolean>;
  readonly genericFileCreateLoading: Signal<boolean>;

  readonly selectedProductId: Signal<string | null>;
  readonly selectedGenericFileId: Signal<string | null>;

  readonly getAllProducts: (includeMarkedForDeletion?: boolean) => void;
  readonly getOneProduct: (input: { id: string; includeMarkedForDeletion?: boolean }) => void;
  readonly updateProduct: (input: { id: string; dto: ProductUpdateRequest }) => void;
  readonly activateProduct: (id: string) => void;
  readonly deactivateProduct: (id: string) => void;
  readonly getAllGenericFiles: (id: string) => void;
  readonly getOneGenericFile: (input: { productId: string; fileId: string }) => void;
  readonly addGenericFile: (input: { fileReq: CreateFileRequest; file: File }) => void;
  readonly downloadGenericFile: (fileId: string) => void;
  readonly resetState: (state: Partial<ProductsState>) => void;
  readonly goToProductActions: (id: string) => Promise<boolean>;
  readonly goToListPage: () => void;
  readonly goToGenericFileActions: (fileId: string) => void;
  readonly goToGenericFileListPage: () => void;
  readonly deleteGenericFile: (input: { productId: string; fileId: string }) => void;
}
